/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/IterationCard" />
import * as React from "react";
import { IterationReason } from "Repos/Common/Contracts/Git";
import { ICommitsTableProps } from "Repos/Common/Contracts/History";
import { getReposRoutingService, IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { IPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { css, IVssComponentContext, useComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Spinner } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { IIteration } from "../../Contracts/PullRequest";
import { getPullRequestViewService } from "../../PullRequestViewService";

const maxCommits = 5;

export interface IterationCardProps {
    className?: string;
    isShim?: boolean;
    iteration: IIteration;
}

export class IterationCard extends VssComponent<IterationCardProps> {
    private prDetailsService: IPullRequestViewService;
    private reposRoutingService: IReposRoutingService;
    private mergeChangesUrl?: string;

    public constructor(props: IterationCardProps, context: IVssComponentContext) {
        super(props);

        this.prDetailsService = getPullRequestViewService(context.pageContext);
        this.reposRoutingService = getReposRoutingService(context.pageContext);

        const targetRepository = this.prDetailsService.getTargetRepository();
        const lastMergeCommit = this.prDetailsService.getLastMergeCommit();
        const mergeTargetCommit = this.prDetailsService.getMergeTargetCommit();
        this.mergeChangesUrl =
            lastMergeCommit &&
            mergeTargetCommit &&
            this.reposRoutingService.getBranchCompareUrl(targetRepository.name, targetRepository.project.name, mergeTargetCommit, lastMergeCommit);
    }

    public render() {
        if (!this.props.isShim) {
            return (
                <Card className={css("bolt-table-card flex-grow", this.props.className)} contentProps={{ contentPadding: false }}>
                    <div className="flex-column flex-grow scroll-hidden">
                        <IterationHeader
                            className={css("iteration-header", this.props.iteration.reason !== IterationReason.ResolveConflicts && "separator-line-bottom")}
                            iteration={this.props.iteration}
                            mergeChangesUrl={this.mergeChangesUrl} />
                        {this.props.iteration.commits.length > 0 && (
                            <WrappedComponent<ICommitsTableProps>
                                wrappedType="repos-commits-table"
                                dependencies={["ms.vss-code-web.commits-list"]}
                                wrappedProps={{
                                    className: "separator-line-bottom iteration-commits-table",
                                    commits: this.props.iteration.commits.slice(0, maxCommits)
                                }}
                                loadingComponent={() => <Spinner className="padding-16" />}
                            />
                        )}
                        {this.props.iteration.commits.length > maxCommits && (
                            <FPSLink className="margin-16 padding-horizontal-4" href={this.props.iteration.commitsUrl}>
                                {Resources.ViewAllCommits}
                            </FPSLink>
                        )}
                    </div>
                </Card>
            );
        }
    
        return (
            <Card className={css("bolt-table-card flex-grow", this.props.className)} contentProps={{ contentPadding: false }}>
                <div className="flex-column flex-grow scroll-hidden">
                    <Spinner className={"padding-16"} />
                </div>
            </Card>
        );
    }
}

function IterationHeader({ className, iteration, mergeChangesUrl }: { className?: string; iteration: IIteration, mergeChangesUrl?: string }): JSX.Element {
    const { pageContext } = useComponentContext();

    let headerMessage: string;
    if ((iteration.reason & IterationReason.Retarget) !== 0) {
        headerMessage = format(
            Resources.ChangedTargetBranch,
            iteration.author.displayName,
            refToBranchName(iteration.oldTargetRefName!),
            refToBranchName(iteration.newTargetRefName!)
        );
    } else if ((iteration.reason & IterationReason.ResolveConflicts) !== 0) {
        headerMessage = format(
            Resources.ConflictsResolved,
            iteration.author.displayName
        );
    } else {
        const messageFormat = iteration.commits.length === 1 ? Resources.PushedOneCommit : Resources.PushedManyCommits;
        const moreCommitsMark = iteration.hasMoreCommits ? "+" : "";
        headerMessage = format(messageFormat, iteration.author.displayName, iteration.commits.length.toFixed() + moreCommitsMark);
    }

    const prViewService = getPullRequestViewService(pageContext);
    const baseIterationId = iteration.id - 1;
    const iterationLink = iteration.reason === IterationReason.ResolveConflicts
        ? mergeChangesUrl
        : prViewService.getFileUrl(iteration.id.toString(), baseIterationId.toString());

    return (
        <FPSLink href={iterationLink} className="iteration-card-header" removeUnderline={true}>
            <div className={css("flex-row rhythm-horizontal-8", className)}>
                <Persona className="margin-top-4 margin-right-4" identity={iteration.author} size={PersonaSize.size24} />
                <div className="flex-column flex-grow text-ellipsis">
                    <Tooltip overflowOnly={true}>
                        <span className="font-weight-semibold text-ellipsis">{headerMessage}</span>
                    </Tooltip>
                    <div className="body-s secondary-text">
                        <Ago date={iteration.updatedDate} format={AgoFormat.Compact} />
                    </div>
                </div>
            </div>
        </FPSLink>
    );
}
